import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getProjectApartmentsApi, getEventApartmentsApi } from "../../api/finance.apartment.api";
import { formatPrice } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";

const STATUS_OPTIONS = [
  { value: "ALL", label: "Tất cả trạng thái" },
  { value: "FREE", label: "Còn trống" },
  { value: "LOCKING", label: "Đã locking" },
  { value: "BOOKING", label: "Đã booking" },
];

const ApartmentSelect = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const projectId = params.get("projectId") || "";
  const eventId = params.get("eventId") || "";
  const mode = params.get("mode") || "booking"; // booking | locking

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const sheet = useDisclosure();
  const info = useDisclosure();
  const [infoItem, setInfoItem] = useState(null);
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const fetchList = useCallback(
    async (kw) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const p = { page: 1, limit: 100, search: kw || undefined };
        let data;
        if (eventId) data = await getEventApartmentsApi({ ...p, saleOpeningEventId: eventId });
        else data = await getProjectApartmentsApi({ ...p, projectId });
        if (rid !== reqId.current) return;
        const list = (data?.items || data || []).map((x) => x.apartment || x);
        setItems(list);
      } catch {
        if (rid === reqId.current) setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [projectId, eventId]
  );

  useEffect(() => { fetchList(search); }, [search, fetchList]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const hasBooking = !!it?.booking;
      const hasLocking = !!it?.locking;
      if (status === "FREE") return !hasBooking && !hasLocking;
      if (status === "LOCKING") return hasLocking && !hasBooking;
      if (status === "BOOKING") return hasBooking;
      return true;
    });
  }, [items, status]);

  const statusLabel = STATUS_OPTIONS.find((s) => s.value === status)?.label || "Tất cả trạng thái";

  const isBooking = mode !== "locking";

  const done = () => {
    // Locking bắt buộc chọn căn; Booking cho phép không chọn (điền dự án ở form)
    if (!isBooking && !selected) return;
    const base = isBooking ? `${EMP_BASE_PATH}/tai-chinh/booking/tao` : `${EMP_BASE_PATH}/tai-chinh/locking/tao`;
    const q = new URLSearchParams();
    if (selected) {
      q.set("apartmentId", selected._id);
      q.set("code", selected.code || "");
    }
    const pid = projectId || selected?.project?._id || "";
    if (pid) q.set("projectId", pid);
    if (eventId) q.set("eventId", eventId);
    navigate(`${base}?${q.toString()}`);
  };

  const pageBody = (
    <>

      <Box px="16px" pt="12px" pb={selected || isBooking ? "120px" : "16px"}>
        <Flex h="50px" align="center" px="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="10px">
          <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
          <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Nhập mã căn hộ ..." onChange={onSearchChange} />
        </Flex>

        <Flex mt="16px" mb="12px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách căn hộ</Text>
          <Flex as="button" align="center" gap="6px" onClick={sheet.onOpen}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{statusLabel}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : filtered.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có căn hộ</Text>
        ) : (
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="12px">
            {filtered.map((it) => {
              const hasBooking = !!it?.booking;
              const hasLocking = !!it?.locking;
              const disabled = hasBooking || hasLocking;
              const isSel = selected?._id === it._id;
              const pill = hasBooking ? { t: "Booking", c: "#EC302A", bg: "#FFECEA" } : hasLocking ? { t: "Locking", c: "#FF9000", bg: "#FFF3E0" } : null;
              return (
                <Box
                  key={it._id}
                  as="button"
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    if (selected?._id === it._id && info.isOpen) {
                      info.onClose();
                    } else {
                      setSelected(it);
                      setInfoItem(it);
                      info.onOpen();
                    }
                  }}
                  textAlign="left"
                  p="12px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={isSel ? APP.primary200 : BORDER}
                  bg={disabled ? "#F5F7FB" : "white"}
                  opacity={disabled ? 0.7 : 1}
                  cursor={disabled ? "not-allowed" : "pointer"}
                >
                  <Flex align="center" justify="space-between">
                    <Text fontSize="16px" fontWeight={700} color={TEXT}>{it?.code || "-"}</Text>
                    {pill && (
                      <Box bg={pill.bg} borderRadius="6px" px="6px" py="2px">
                        <Text fontSize="10px" fontWeight={600} color={pill.c}>{pill.t}</Text>
                      </Box>
                    )}
                  </Flex>
                  <Text fontSize="12px" color={MUTED} mt="6px">DT: {it?.area || 0}m²</Text>
                  <Text fontSize="12px" color={MUTED} mt="2px">CT: {it?.numberOfBedrooms || 0}PN, {it?.numberOfToilets || 0}VS</Text>
                  <Text fontSize="13px" fontWeight={600} color={APP.primary200} mt="6px">{it?.totalValue ? formatPrice(it.totalValue) : "0"}</Text>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Bar Hoàn thành: ghim đáy, hiện khi popup đóng và (đang chọn, hoặc là booking) */}
      {!info.isOpen && (selected || isBooking) && (
        <Box position={{ base: "fixed", md: "static" }} bottom={{ base: "0", md: "auto" }} left={{ base: "50%", md: "auto" }} transform={{ base: "translateX(-50%)", md: "none" }} w="100%" maxW={{ base: "480px", md: "100%" }} bg="white" borderTop="1px solid #EEF1F6" px="16px" pt="10px" pb="16px" zIndex={20} boxShadow="0 -4px 12px rgba(0,0,0,0.06)">
          {selected && (
            <Flex mb="10px" align="center" gap="6px" flexWrap="wrap">
              <Text fontSize="13px" color={MUTED}>Đã chọn:</Text>
              <Text fontSize="13px" fontWeight={600} color={TEXT}>{selected.code}</Text>
              <Box w="4px" h="4px" borderRadius="full" bg={APP.primary200} />
              <Text fontSize="13px" fontWeight={600} color={APP.primary200}>{selected.totalValue ? formatPrice(selected.totalValue) : "0"}</Text>
            </Flex>
          )}
          <Flex as="button" type="button" onClick={done} w="100%" h="48px" align="center" justify="center" borderRadius="8px" bg={APP.primary200} color="white" fontWeight={700}>
            Hoàn thành
          </Flex>
        </Box>
      )}

      {/* Sheet chi tiết căn hộ */}
      <Drawer isOpen={info.isOpen} onClose={info.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="18px" fontWeight={700} color={TEXT} mb="6px">{infoItem?.name || infoItem?.code || "-"}</Text>
            {(() => {
              const rows = [
                infoItem?.totalValue ? { label: "Giá bán niêm yết", value: `${new Intl.NumberFormat("vi-VN").format(infoItem.totalValue)} VNĐ` } : null,
                infoItem?.area ? { label: "Diện tích thông thuỷ", value: `${infoItem.area}m²` } : null,
                infoItem?.width ? { label: "Chiều rộng", value: `${infoItem.width}m` } : null,
                infoItem?.length ? { label: "Chiều dài", value: `${infoItem.length}m` } : null,
                infoItem?.numberOfBedrooms ? { label: "Số phòng ngủ", value: infoItem.numberOfBedrooms } : null,
                infoItem?.numberOfToilets ? { label: "Số vệ sinh", value: infoItem.numberOfToilets } : null,
              ].filter(Boolean);
              return rows.length ? rows.map((r, i) => (
                <Flex key={r.label} justify="space-between" align="center" py="16px" borderBottom={i === rows.length - 1 ? "none" : "1px solid #EEF1F6"}>
                  <Text fontSize="14px" color="#51607B">{r.label}</Text>
                  <Text fontSize="14px" fontWeight={600} color={TEXT}>{r.value}</Text>
                </Flex>
              )) : (
                <Text fontSize="14px" color={MUTED} py="16px">Chưa có thông tin chi tiết</Text>
              );
            })()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">Trạng thái căn hộ</Text>
            {STATUS_OPTIONS.map((o, i) => {
              const active = o.value === status;
              const last = i === STATUS_OPTIONS.length - 1;
              return (
                <Flex key={o.value} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={last ? "none" : "1px solid #EEF1F6"} onClick={() => { setStatus(o.value); sheet.onClose(); }}>
                  <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400}>{o.label}</Text>
                  {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                </Flex>
              );
            })}
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chọn căn hộ" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh">
      <AppBar title="Chọn căn hộ" onBack={() => navigate(-1)} />
      {pageBody}
    </Box>
  );
};

export default ApartmentSelect;
